import { useEffect, useState } from 'react';

import {
  ArrowButton, Button, ColumnSortEnum, Modal, Table, TableMetadata,
  TableStateWithFilters,
  TextField, Typography, useTable
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ExportTypeEnum } from '@king-ict/eupisi-ui/models';
import { nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { deleteLanguage, getLanguages, exportLanguages } from '../../api/nisooApi';
import { useDidUpdateEffect, useInput } from "../../hooks";
import { CodeBookExtended, CodeBookSearch } from '../../models/CodeBook';
import { useGetUserSessionQuery } from '../../queries/userQuery';
import { codebookRouteLanguage } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookLanguage(): JSX.Element {
  const storageKey = 'CodeBookLanguageSearchTableState';
  const defaultPageSaze = 10;

  const createInitialTableState = (): TableStateWithFilters<CodeBookSearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        name: "",
        page: 1,
        pageSize: defaultPageSaze,
        sortBy: 1,
        order: ColumnSortEnum.ASC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<CodeBookSearch>;
    return result;
  }

  const createInitialSearch = (initialTableState: TableStateWithFilters<CodeBookSearch>): CodeBookSearch => {
    return {
      name: initialTableState.name
    }
  }

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const { data: userSession } = useGetUserSessionQuery();
  const [initialTableState] = useState<TableStateWithFilters<CodeBookSearch>>(() => createInitialTableState());
  const [searchFilter, setSearchFilter] = useState<CodeBookSearch>(() => createInitialSearch(initialTableState));
  const [tableLanguages, setTableLanguages] = useState<CodeBookExtended[]>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState<CodeBookExtended>()

  const [languages, setLanguages] = useState<CodeBookExtended[]>([]);
  const [itemCount, setItemCount] = useState(languages?.length ?? 0);
  const userCanEditData = false;

  const callDataFetch = async () => {
    var result = await getLanguages();
    setLanguages(result);
    setItemCount(result?.length ?? 0);
  }

  const tableMetadata: TableMetadata<CodeBookExtended> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "name",
        header: t("codeBook.language"),
        accessor: "name",
        sortBy: 1
      },
      {
        columnKey: "canDelete",
        header: "",
        accessor: (item) => {
          return (
            <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={() => deleteItem(item)}>
              {item.canDelete ? t("common.remove") : ""}
            </Typography>
          )
        }
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <ArrowButton ariaLabel={t("codeBook.languages.goto")}
              onClick={() => { navigateToDetails(item) }}
            />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  var [tableProps, tableState, setFilters] = useTable<CodeBookSearch>(
    {
      metadata: tableMetadata,
      data: tableLanguages,
      pagination: true,
      paginationPage: initialTableState.page as any,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: itemCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  const search = async () => {
    setFilters(searchFilter);
  };

  const clearFilters = async () => {
    const clearSearchFilter = { name: "" }
    setSearchFilter(clearSearchFilter);
    sessionStorage.setItem(storageKey, JSON.stringify({
      name: "",
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
    setTableLanguages(languages);
    setFilters(clearSearchFilter);
  };


  useEffect(() => {
    callDataFetch();
  }, [tableState, userSession]);

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!languages) {
      return;
    }
    const filteredLanguages = () => {
      let filter = tableState.name ? tableState.name?.toLowerCase() : "";

      let filteredLanguages = languages
        .filter((item) => item.name.toLowerCase()
          .includes(filter))
        .sort((x, y) => tableState.order === ColumnSortEnum.ASC ? x.name.localeCompare(y.name) : (-1) * x.name.localeCompare(y.name));

      setItemCount(filteredLanguages.length);
      let pageSize = (tableProps.paginationProps?.pageSize ?? 0);
      let startIndex = ((tableProps.paginationProps?.page ?? 1) - 1) * pageSize;
      let endIndex = startIndex + pageSize;
      return filteredLanguages.slice(startIndex, endIndex);
    }
    setTableLanguages(filteredLanguages());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, languages]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...searchFilter,
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
  }, [tableState])

  const navigateToDetails = (item: CodeBookExtended) => {
    history.push(
      codebookRouteLanguage(),
      {
        page: "details",
        language: item
      }
    );
  }

  const deleteItem = async (item: CodeBookExtended) => {
    if (!item.canDelete) {
      return;
    }

    setLanguageToDelete(item);
    setOpenConfirmModal(true);
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    await deleteLanguage(languageToDelete!.id!);
    await callDataFetch();
  }

  const navigateToAddNewRecord = () => {
    history.push(
      codebookRouteLanguage(),
      {
        page: "edit"
      }
    );
  };

  const download = async (exportType: ExportTypeEnum) => {
    exportLanguages(exportType);
  }

  return (
    <>
      <Row className="mt-5">
        <Col md={8}>
          <h3>{t("codeBook.language")}</h3>
        </Col>
        {userCanEditData &&
          <Col md={4} className={styles.addButonCol}>
            <Button outline icon={<AddIcon />} onClick={navigateToAddNewRecord}>
              {t("common.addNewRecord")}
            </Button>
          </Col>
        }
      </Row>
      <Row className="mt-3">
        <Col md={6}>
          <TextField
            label={t("codeBook.language")}
            name={nameof<typeof searchFilter>("name")}
            value={searchFilter.name}
            maxLength={50}
            onChange={handleChange(setSearchFilter)}
          />
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-center">
          <Button onClick={search}>
            {t("common.search")}
          </Button>
          <Typography color="primary" className={styles.clearFilters + " ml-5"} onClick={clearFilters}>
            {t("common.clearFilters")}
          </Typography>
        </Col>
      </Row>
      <Table  {...tableProps} />
      <Row className="mt-5">
        <Col md={12} className="d-flex justify-content-end align-items-center">
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Csv)}
          >
            {t("common.downloadDocumentExtension", { extension: ".csv" })}
          </Button>
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Excel)}
          >
            {t("common.downloadDocumentExtension", { extension: ".xslx" })}
          </Button>
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesDelete")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          {
            languageToDelete &&
            <Typography component="h1" variant="largeBold">
              {t("codeBook.languages.areYouSureDelete", { name: languageToDelete.name })}
            </Typography>
          }

        </div>
      </Modal>
    </>
  );
}

